import React from 'react'
import { CheckCircle2, DollarSign, PiggyBank, Send, Wallet } from 'lucide-react'
import { money, fmtDay } from '../financial/formatting'
import { hasSplit, payoutTotal } from './model'

// The headline figures for the range. "Your payout" is everything the
// partner is paid, never a smaller number: under an agreement the part that
// fell outside the split (`outside_split`, paid at 100%) is added back in and
// said out loud underneath, rather than left for the partner to discover as
// money that seems to be missing.
export default function KpiRow({ kpis, split }) {
  const withSplit = hasSplit(kpis)
  const outside = Number(kpis.outside_split || 0)
  const showBalance = kpis.to_balance !== undefined

  const cards = [
    {
      key: 'sent', icon: <Send size={16} />, label: 'Leads sent',
      value: Number(kpis.leads_sent || 0).toLocaleString('en-US'),
    },
    {
      key: 'sold', icon: <CheckCircle2 size={16} />, label: 'Leads sold',
      value: Number(kpis.leads_sold || 0).toLocaleString('en-US'),
      sub: `${Number(kpis.sale_rate_pct || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}% sale rate`,
    },
    {
      key: 'value', icon: <DollarSign size={16} />, label: 'Total lead value',
      value: money(kpis.lead_value),
    },
    {
      key: 'payout', icon: <Wallet size={16} />, label: 'Your payout', accent: true,
      value: money(payoutTotal(kpis)),
      sub: withSplit && outside > 0
        ? `Includes ${money(outside)} paid in full${split?.accrual_start ? ` (before ${fmtDay(split.accrual_start)})` : ''}`
        : null,
    },
    showBalance && {
      key: 'balance', icon: <PiggyBank size={16} />, label: 'Applied to balance',
      value: money(kpis.to_balance),
    },
  ].filter(Boolean)

  return (
    <div className="pd-kpis" style={{ '--pd-kpi-count': cards.length }}>
      {cards.map((c) => <KpiCard key={c.key} {...c} />)}
    </div>
  )
}

function KpiCard({ icon, label, value, sub, accent }) {
  return (
    <div className="card" data-testid="kpi" style={{
      padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0,
      borderColor: accent ? 'var(--accent)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent ? 'var(--accent)' : 'var(--text-muted)' }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </span>
      </div>
      <div style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 800,
        color: 'var(--text-primary)', lineHeight: 1.15,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{sub}</div>}
    </div>
  )
}
